import React, { useEffect, useState, useCallback } from 'react';
import { FiFileText, FiRefreshCw } from 'react-icons/fi';
import {
  Employee, listEmployees, generateT4s, listT4s, t4PdfUrl, generateRoe, listRoes, roePdfUrl,
} from './payrollApi';

const money = (n: any) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Common ROE reason codes (Service Canada Block 16).
const ROE_REASONS = [
  ['A', 'Shortage of work / layoff'], ['D', 'Illness or injury'], ['E', 'Quit'],
  ['F', 'Maternity'], ['G', 'Retirement'], ['K', 'Other'], ['M', 'Dismissal'], ['N', 'Leave of absence'],
];

export default function YearEndTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [t4s, setT4s] = useState<any[]>([]);
  const [roes, setRoes] = useState<any[]>([]);
  const [emps, setEmps] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [roeEmp, setRoeEmp] = useState<number | ''>('');
  const [roeReason, setRoeReason] = useState('A');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [t, r, e] = await Promise.all([listT4s(year), listRoes(), listEmployees()]);
      setT4s(t); setRoes(r); setEmps(e);
    } catch (err: any) { setError(err.message || 'Could not load'); }
    finally { setLoading(false); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const doGenT4 = async () => {
    setBusy('t4'); setError('');
    try { const r = await generateT4s(year); await load(); alert(`Generated ${r.generated} T4 slip${r.generated === 1 ? '' : 's'} from year-to-date totals.`); }
    catch (e: any) { setError(e.message || 'Could not generate T4s'); }
    finally { setBusy(''); }
  };
  const doGenRoe = async () => {
    if (!roeEmp) { setError('Pick an employee for the ROE.'); return; }
    setBusy('roe'); setError('');
    try { await generateRoe(Number(roeEmp), roeReason); await load(); setRoeEmp(''); }
    catch (e: any) { setError(e.message || 'Could not generate ROE'); }
    finally { setBusy(''); }
  };

  return (
    <div>
      <div className="pay-toolbar">
        <div className="pay-field" style={{ maxWidth: 130 }}><label>Tax year</label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[year + 1, year, year - 1, year - 2].filter((v, i, a) => a.indexOf(v) === i).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      {error && <div className="pay-error">{error}</div>}
      <div className="pay-note" style={{ marginBottom: 'var(--spacing-md)' }}>
        T4/ROE PDFs are <strong>review copies</strong> generated from approved payroll. File the official T4 with CRA and submit ROEs via Service Canada (ROE Web).
      </div>

      <div className="pay-toolbar">
        <h3 style={{ margin: 0 }}>T4 slips — {year}</h3>
        <span className="spacer" />
        <button className="pay-btn" onClick={doGenT4} disabled={!!busy}><FiRefreshCw /> {busy === 't4' ? 'Generating…' : 'Generate / refresh T4s'}</button>
      </div>
      <div className="pay-card" style={{ padding: 0, overflowX: 'auto', marginBottom: 'var(--spacing-xl)' }}>
        {loading ? <div className="pay-empty">Loading…</div>
          : t4s.length === 0 ? <div className="pay-empty">No T4s yet — click “Generate T4s” (needs approved payroll for the year).</div>
          : (
          <table className="pay-table">
            <thead><tr><th>Employee</th><th className="num">Box 14 income</th><th className="num">Box 16 CPP</th><th className="num">Box 18 EI</th><th className="num">Box 22 tax</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {t4s.map((t) => (
                <tr key={t.pay_t4_id}>
                  <td>{t.first_name} {t.last_name}</td>
                  <td className="num">{money(t.box14_employment_income)}</td>
                  <td className="num">{money(t.box16_cpp)}</td>
                  <td className="num">{money(t.box18_ei)}</td>
                  <td className="num">{money(t.box22_income_tax)}</td>
                  <td><span className={`pay-badge ${t.status === 'issued' ? 'posted' : 'calculated'}`}>{t.status}</span></td>
                  <td className="num"><a className="pay-btn ghost sm" href={t4PdfUrl(t.pay_t4_id)} target="_blank" rel="noreferrer"><FiFileText /> PDF</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <h3>Records of Employment (ROE)</h3>
      <div className="pay-card" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <div className="pay-grid">
          <div className="pay-field span2"><label>Employee</label>
            <select value={roeEmp} onChange={(e) => setRoeEmp(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— pick —</option>
              {emps.map((e) => <option key={e.pay_employee_id} value={e.pay_employee_id}>{e.first_name} {e.last_name}</option>)}
            </select>
          </div>
          <div className="pay-field"><label>Reason</label>
            <select value={roeReason} onChange={(e) => setRoeReason(e.target.value)}>
              {ROE_REASONS.map(([c, l]) => <option key={c} value={c}>{c} — {l}</option>)}
            </select>
          </div>
          <div className="pay-field" style={{ justifyContent: 'flex-end' }}>
            <button className="pay-btn" onClick={doGenRoe} disabled={!!busy}>{busy === 'roe' ? 'Generating…' : 'Generate ROE'}</button>
          </div>
        </div>
      </div>
      <div className="pay-card" style={{ padding: 0, overflowX: 'auto' }}>
        {roes.length === 0 ? <div className="pay-empty">No ROEs yet.</div> : (
          <table className="pay-table">
            <thead><tr><th>Employee</th><th>Last day</th><th>Reason</th><th className="num">Insurable hrs</th><th className="num">Insurable earnings</th><th></th></tr></thead>
            <tbody>
              {roes.map((r) => (
                <tr key={r.pay_roe_id}>
                  <td>{r.first_name} {r.last_name}</td>
                  <td>{(r.last_day_worked || '').slice(0, 10)}</td>
                  <td>{r.reason_code}</td>
                  <td className="num">{Number(r.total_insurable_hours || 0)}</td>
                  <td className="num">{money(r.total_insurable_earnings)}</td>
                  <td className="num"><a className="pay-btn ghost sm" href={roePdfUrl(r.pay_roe_id)} target="_blank" rel="noreferrer"><FiFileText /> PDF</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
